import { useEffect, useRef, useCallback } from 'react';

/**
 * useNotificationsWS
 * Connects to the backend WebSocket /api/ws/notifications and invokes onMessage for each event.
 * Automatically reconnects with exponential backoff. Expects a JWT token from localStorage.
 */
export const useNotificationsWS = (onMessage) => {
  const wsRef = useRef(null);
  const retryRef = useRef(0);
  const stoppedRef = useRef(false);
  const handlerRef = useRef(onMessage);

  useEffect(() => {
    handlerRef.current = onMessage;
  }, [onMessage]);

  const connect = useCallback(() => {
    if (stoppedRef.current) return;
    const token = localStorage.getItem('token');
    if (!token) return;

    const backend = process.env.REACT_APP_BACKEND_URL || '';
    // Convert http(s) to ws(s)
    const wsUrl = backend.replace(/^http/, 'ws') + `/api/ws/notifications?token=${encodeURIComponent(token)}`;

    try {
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        retryRef.current = 0;
      };

      ws.onmessage = (evt) => {
        try {
          const data = JSON.parse(evt.data);
          if (handlerRef.current) handlerRef.current(data);
        } catch {
          // ignore non-JSON
        }
      };

      ws.onclose = () => {
        if (stoppedRef.current) return;
        const delay = Math.min(1000 * Math.pow(2, retryRef.current), 15000);
        retryRef.current += 1;
        setTimeout(connect, delay);
      };

      ws.onerror = () => {
        try { ws.close(); } catch { /* noop */ }
      };
    } catch (e) {
      // Retry
      const delay = Math.min(1000 * Math.pow(2, retryRef.current), 15000);
      retryRef.current += 1;
      setTimeout(connect, delay);
    }
  }, []);

  useEffect(() => {
    stoppedRef.current = false;
    connect();
    return () => {
      stoppedRef.current = true;
      if (wsRef.current) {
        try { wsRef.current.close(); } catch { /* noop */ }
      }
    };
  }, [connect]);
};

export default useNotificationsWS;
